import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Promotion List Component
 *
 * List and manage product promotions (M3 Wave 6)
 *
 * @package WooAIChatbot
 */
import { useState } from 'react';
import { Plus, Edit, Trash2, Calendar, AlertCircle, Loader2 } from 'lucide-react';
import { useAsync } from '../../../utils/hooks/useAsync';
import { promotionsApi } from '../../../utils/api';
import { useTranslation } from '../../../hooks/useTranslation';
// Inline styles
const styles = {
    page: {
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 500,
        color: '#111827',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0,
    },
    newButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '36px',
        padding: '0 12px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    filterButtons: {
        display: 'flex',
        gap: '8px',
    },
    filterButton: {
        height: '40px',
        padding: '0 16px',
        borderRadius: '9999px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#4b5563',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    filterButtonActive: {
        height: '40px',
        padding: '0 16px',
        borderRadius: '9999px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    tableHeader: {
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
    },
    th: {
        padding: '12px 24px',
        textAlign: 'left',
        fontSize: '12px',
        fontWeight: 600,
        color: '#374151',
    },
    thRight: {
        padding: '12px 24px',
        textAlign: 'right',
        fontSize: '12px',
        fontWeight: 600,
        color: '#374151',
    },
    tr: {
        borderBottom: '1px solid #f3f4f6',
    },
    td: {
        padding: '16px 24px',
    },
    promotionCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    promotionName: {
        fontWeight: 500,
        fontSize: '16px',
        color: '#111827',
    },
    promotionDate: {
        fontSize: '12px',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    badgeContainer: {
        display: 'flex',
        gap: '8px',
    },
    badge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: 500,
    },
    badgeManual: {
        backgroundColor: '#dbeafe',
        color: '#1e40af',
    },
    badgeAutomatic: {
        backgroundColor: '#e0e7ff',
        color: '#4338ca',
    },
    statusCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    switchContainer: {
        position: 'relative',
        width: '44px',
        height: '24px',
    },
    switch: {
        width: '44px',
        height: '24px',
        borderRadius: '9999px',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    switchOn: {
        backgroundColor: '#6366f1',
    },
    switchOff: {
        backgroundColor: '#d1d5db',
    },
    switchThumb: {
        position: 'absolute',
        top: '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#ffffff',
        transition: 'transform 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        pointerEvents: 'none',
    },
    switchThumbOn: {
        transform: 'translateX(22px)',
    },
    switchThumbOff: {
        transform: 'translateX(2px)',
    },
    statusText: {
        fontSize: '14px',
        color: '#4b5563',
    },
    actionsCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
    },
    actionButton: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    deleteButton: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    loading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 0',
    },
    loadingText: {
        marginLeft: '12px',
        fontSize: '14px',
        color: '#4b5563',
    },
    emptyState: {
        textAlign: 'center',
        padding: '48px 0',
    },
    emptyText: {
        fontSize: '14px',
        color: '#6b7280',
    },
    errorCard: {
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid #fecaca',
        backgroundColor: '#fef2f2',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
    },
    errorTitle: {
        fontWeight: 600,
        color: '#991b1b',
        margin: 0,
    },
    errorMessage: {
        fontSize: '14px',
        color: '#b91c1c',
        margin: '4px 0 0 0',
    },
    retryButton: {
        marginTop: '12px',
        height: '36px',
        padding: '0 16px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        backgroundColor: '#ffffff',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
};
// Toggle switch component
function ToggleSwitch({ checked, onChange }) {
    return (_jsxs("div", { style: styles.switchContainer, onClick: onChange, children: [_jsx("div", { style: { ...styles.switch, ...(checked ? styles.switchOn : styles.switchOff) } }), _jsx("div", { style: { ...styles.switchThumb, ...(checked ? styles.switchThumbOn : styles.switchThumbOff) } })] }));
}
export function PromotionList({ onEdit, onCreate }) {
    const { t } = useTranslation();
    const [activeFilter, setActiveFilter] = useState('all');
    // Fetch promotions from backend
    const { data: promotions, loading, error, execute: refreshPromotions } = useAsync(() => promotionsApi.getAll(), [], true);
    // Toggle promotion active status
    const handleToggle = async (id, _currentStatus) => {
        try {
            await promotionsApi.toggle(id);
            await refreshPromotions();
        }
        catch (err) {
            console.error('Failed to toggle promotion:', err);
            alert(t('failedToTogglePromotion'));
        }
    };
    // Delete promotion handler
    const handleDelete = async (id, name) => {
        if (!confirm(`${t('confirmDelete')} "${name}"? ${t('actionCannotBeUndone')}`)) {
            return;
        }
        try {
            await promotionsApi.delete(id);
            await refreshPromotions();
        }
        catch (err) {
            console.error('Failed to delete promotion:', err);
            alert(t('failedToDeletePromotion'));
        }
    };
    // Filter promotions based on active status
    const filteredPromotions = promotions?.filter((promotion) => {
        if (activeFilter === 'all')
            return true;
        if (activeFilter === 'active')
            return promotion.active;
        if (activeFilter === 'inactive')
            return !promotion.active;
        return true;
    }) || [];
    // Count promotions by status
    const statusCounts = {
        all: promotions?.length || 0,
        active: promotions?.filter(p => p.active).length || 0,
        inactive: promotions?.filter(p => !p.active).length || 0,
    };
    // Format date range
    const formatDateRange = (from, to) => {
        const formatDate = (dateStr) => {
            if (!dateStr)
                return null;
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        };
        const fromFormatted = formatDate(from);
        const toFormatted = formatDate(to);
        if (fromFormatted && toFormatted) {
            return `${fromFormatted} - ${toFormatted}`;
        }
        else if (fromFormatted) {
            return `${t('from')} ${fromFormatted}`;
        }
        else if (toFormatted) {
            return `${t('until')} ${toFormatted}`;
        }
        return t('noDateRange');
    };
    return (_jsxs("div", { style: styles.page, children: [_jsxs("div", { style: styles.header, children: [_jsxs("div", { style: styles.headerContent, children: [_jsx("h1", { style: styles.title, children: t('promotions') }), _jsx("p", { style: styles.subtitle, children: t('promotionsDescription') })] }), _jsxs("button", { style: styles.newButton, onClick: onCreate, disabled: loading, children: [_jsx(Plus, { style: { width: '16px', height: '16px' } }), t('newPromotion')] })] }), error && (_jsxs("div", { style: styles.errorCard, children: [_jsx(AlertCircle, { style: { width: '20px', height: '20px', color: '#dc2626', flexShrink: 0, marginTop: '2px' } }), _jsxs("div", { style: { flex: 1 }, children: [_jsx("h3", { style: styles.errorTitle, children: t('errorLoadingPromotions') }), _jsx("p", { style: styles.errorMessage, children: error.message }), _jsx("button", { onClick: refreshPromotions, style: styles.retryButton, children: t('tryAgain') })] })] })), _jsxs("div", { style: styles.filterButtons, children: [_jsxs("button", { style: activeFilter === 'all' ? styles.filterButtonActive : styles.filterButton, onClick: () => setActiveFilter('all'), disabled: loading, children: [t('all'), " (", statusCounts.all, ")"] }), _jsxs("button", { style: activeFilter === 'active' ? styles.filterButtonActive : styles.filterButton, onClick: () => setActiveFilter('active'), disabled: loading, children: [t('active'), " (", statusCounts.active, ")"] }), _jsxs("button", { style: activeFilter === 'inactive' ? styles.filterButtonActive : styles.filterButton, onClick: () => setActiveFilter('inactive'), disabled: loading, children: [t('inactive'), " (", statusCounts.inactive, ")"] })] }), _jsx("div", { style: styles.card, children: loading ? (_jsxs("div", { style: styles.loading, children: [_jsx(Loader2, { style: { width: '32px', height: '32px', color: '#6366f1', animation: 'spin 1s linear infinite' } }), _jsx("span", { style: styles.loadingText, children: t('loadingPromotions') })] })) : filteredPromotions.length === 0 ? (_jsx("div", { style: styles.emptyState, children: _jsx("p", { style: styles.emptyText, children: activeFilter !== 'all'
                            ? t('noFilteredPromotionsFound', { filter: t(activeFilter) })
                            : t('noPromotionsYet') }) })) : (_jsx("div", { style: { overflowX: 'auto' }, children: _jsxs("table", { style: styles.table, children: [_jsx("thead", { children: _jsxs("tr", { style: styles.tableHeader, children: [_jsx("th", { style: styles.th, children: t('name') }), _jsx("th", { style: styles.th, children: t('type') }), _jsx("th", { style: styles.th, children: t('status') }), _jsx("th", { style: styles.th, children: t('dateRange') }), _jsx("th", { style: styles.thRight, children: t('actions') })] }) }), _jsx("tbody", { children: filteredPromotions.map((promotion) => (_jsxs("tr", { style: styles.tr, children: [_jsx("td", { style: styles.td, children: _jsxs("div", { style: styles.promotionCell, children: [_jsx("div", { style: styles.promotionName, children: promotion.name }), _jsxs("div", { style: styles.promotionDate, children: [t('boost'), ": ", promotion.boost_multiplier, "x"] })] }) }), _jsx("td", { style: styles.td, children: _jsx("div", { style: styles.badgeContainer, children: _jsx("span", { style: {
                                                        ...styles.badge,
                                                        ...(promotion.type === 'manual' ? styles.badgeManual : styles.badgeAutomatic)
                                                    }, children: promotion.type === 'manual' ? t('manual') : t('automatic') }) }) }), _jsx("td", { style: styles.td, children: _jsxs("div", { style: styles.statusCell, children: [_jsx(ToggleSwitch, { checked: promotion.active, onChange: () => handleToggle(promotion.id, promotion.active) }), _jsx("span", { style: styles.statusText, children: promotion.active ? t('active') : t('inactive') })] }) }), _jsx("td", { style: styles.td, children: _jsxs("div", { style: styles.promotionDate, children: [_jsx(Calendar, { style: { width: '14px', height: '14px' } }), formatDateRange(promotion.valid_from, promotion.valid_to)] }) }), _jsx("td", { style: styles.td, children: _jsxs("div", { style: styles.actionsCell, children: [_jsx("button", { style: styles.actionButton, "aria-label": t('editPromotion'), onClick: () => onEdit(promotion.id), title: t('editPromotion'), children: _jsx(Edit, { style: { width: '16px', height: '16px', color: '#4b5563' } }) }), _jsx("button", { style: styles.deleteButton, "aria-label": t('deletePromotion'), onClick: () => handleDelete(promotion.id, promotion.name), title: t('deletePromotion'), children: _jsx(Trash2, { style: { width: '16px', height: '16px', color: '#dc2626' } }) })] }) })] }, promotion.id))) })] }) })) })] }));
}
export default PromotionList;
